import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/api/client';
// UsersManagement removed - using GHL users only
import unifiedPrintingService from '@/utils/unifiedPrintingService';
import { Printer, Loader2, RefreshCw, Wifi } from 'lucide-react';
import stripeTerminal from '@/utils/stripeTerminal';
import { BACKEND_URL } from '@/config';

export default function Settings() {
  const { toast } = useToast();
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [taxRate, setTaxRate] = useState(0.075);
  const [adminSettingsLoaded, setAdminSettingsLoaded] = useState(false);
  const [brand, setBrand] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [checkingStripe, setCheckingStripe] = useState(true);
  const [qzConnected, setQzConnected] = useState(false);
  
  // Stripe Terminal Reader
  const [connectedReader, setConnectedReader] = useState(() => {
    const saved = localStorage.getItem('stripe_terminal_reader');
    return saved ? JSON.parse(saved) : null;
  });
  const [discoveringReaders, setDiscoveringReaders] = useState(false);
  const [discoveredReaders, setDiscoveredReaders] = useState([]);
  const [showReaderDialog, setShowReaderDialog] = useState(false);
  const [connectingReader, setConnectingReader] = useState(false);
  const [readerStatus, setReaderStatus] = useState(null);
  
  // Card Payment Mode Setting
  const [cardPaymentMode, setCardPaymentMode] = useState(() => {
    const saved = localStorage.getItem('card_payment_mode');
    return saved || 'reader_first'; // 'reader_first' or 'manual_entry'
  });
  
  // Credit Card Fee Pass-Through Settings
  const [ccFeeEnabled, setCcFeeEnabled] = useState(false);
  const [ccFeeRate, setCcFeeRate] = useState(3.0); // Percentage (e.g. 3.0 = 3%)
  
  // Receipt Settings (split into admin and device settings)
  const [receiptSettings, setReceiptSettings] = useState(() => {
    const saved = localStorage.getItem('receipt_settings');
    return saved ? JSON.parse(saved) : {
      // Admin settings (shared)
      showLogo: true,
      showPhone: true,
      showEmail: true,
      showBarcode: true,
      thankYouMessage: 'Thank you for your business!',
      allowCompleteWithoutPrinting: false,
      useBackgroundWorker: false,
      // Device settings (per printer)
      leftMargin: 5,
      rightMargin: 5,
      selectedPrinter: '',
      openCashDrawer: true,
      // Not stored (printer handles)
      topMargin: 0,
      bottomMargin: 0
    };
  });
  
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // Product Display Settings
  const [displaySettings, setDisplaySettings] = useState(() => {
    const s = localStorage.getItem('display_settings');
    const settings = s ? JSON.parse(s) : {
      columns: 4,
      showImages: true,
      viewMode: 'grid' // 'grid' or 'list'
    };
    console.log('[Settings Init] Display settings loaded:', settings);
    return settings;
  });

  // Customer Rewards Settings
  const [rewardsSettings, setRewardsSettings] = useState({
    enabled: false,
    checkboxFieldId: '',
    checkboxFieldName: '',
    checkboxOptionValue: 'Yes',
    dateFieldId: '',
    dateFieldName: '',
    folderId: '',
    folderName: '',
    folderFieldNames: ''
  });
  const [showRewardsInstructions, setShowRewardsInstructions] = useState(false);
  
  // Print rewards instructions in a clean format
  const printRewardsInstructions = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Castle POS - Customer Rewards Setup Instructions</title>
          <style>
            body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
            h1 { color: #2563eb; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
            h2 { color: #1e40af; margin-top: 30px; border-bottom: 2px solid #ddd; padding-bottom: 5px; }
            h3 { color: #1e3a8a; margin-top: 20px; }
            .field-box { background: #f0f9ff; border-left: 4px solid #2563eb; padding: 15px; margin: 15px 0; }
            .field-box h3 { margin-top: 0; }
            ul, ol { margin: 10px 0; padding-left: 25px; }
            li { margin: 8px 0; }
            code { background: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: monospace; }
            .warning { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; }
            .tip { background: #f3e8ff; border-left: 4px solid #9333ea; padding: 15px; margin: 20px 0; }
            @media print { 
              body { margin: 20px; padding: 0; max-width: 100%; } 
            }
          </style>
        </head>
        <body>
          <h1>Customer Rewards Setup Instructions</h1>
          
          <h2>Overview</h2>
          <p>Castle POS integrates with your CRM's custom fields to manage customer rewards. You'll need to create two custom fields in your CRM account before using the rewards feature.</p>
          
          <div class="tip" style="background: #dbeafe; border-left: 4px solid #2563eb;">
            <h3 style="margin-top: 0; color: #1e40af;">📋 Important: What This Feature Does</h3>
            <p><strong>Castle POS provides the enrollment interface:</strong></p>
            <ul style="margin: 10px 0;">
              <li>Enroll customers into your rewards program</li>
              <li>View all enrolled rewards members</li>
              <li>Look up customer information quickly</li>
            </ul>
            <p><strong>You control the rewards tracking:</strong> The actual rewards program (points, visit tracking, special offers, etc.) is managed through your CRM using custom fields and automations. The POS simply marks customers as "enrolled" - you define what rewards they get and how to track them using your CRM's automation features.</p>
          </div>
          
          <h2>Requirements</h2>
          <p><strong>You need to create two custom fields in your CRM:</strong></p>
          <ul>
            <li><strong>1. Checkbox Field</strong> - Tracks rewards enrollment status</li>
            <li><strong>2. Date Picker Field</strong> - Records enrollment date</li>
          </ul>
          <p><em>Note: You can name these fields anything you want. Use the "Find Field IDs" button to select them after creation.</em></p>
          
          <h2>Recommended Field Setup (Examples)</h2>
          
          <div class="field-box">
            <h3>Example 1: Checkbox Field</h3>
            <ul>
              <li><strong>Field Name:</strong> "Activate Rewards Member" (Your choice)</li>
              <li><strong>Option Name:</strong> "Yes" (Your choice)</li>
              <li><strong>Example Unique ID:</strong> <code>contact.activate_rewards_member</code></li>
            </ul>
          </div>
          
          <div class="field-box">
            <h3>Example 2: Date Picker Field</h3>
            <ul>
              <li><strong>Field Name:</strong> "Member Since" (Your choice)</li>
              <li><strong>Example Unique ID:</strong> <code>contact.member_since</code></li>
            </ul>
          </div>
          
          <h2>How to Set Up in Your CRM</h2>
          <ol>
            <li>Log into your CRM account</li>
            <li>Navigate to <strong>Settings → Custom Fields</strong></li>
            <li>
              <strong>Create a Custom Fields Folder (Recommended):</strong>
              <ul>
                <li>Click "Add Folder"</li>
                <li>Name it "Customer Rewards" or "POS Fields" (Your choice)</li>
                <li>This keeps your rewards fields organized</li>
                <li>The POS will return all fields in this folder</li>
              </ul>
            </li>
            <li>Click <strong>"Add Field"</strong></li>
            <li>Create a <strong>Checkbox field</strong> with option "Yes" (Your choice)</li>
            <li>Create a <strong>Date field</strong> (for enrollment date) - Optionally assign to your "Customer Rewards" folder</li>
            <li>Return to the POS settings page</li>
            <li>Click the <strong>"Find Field IDs"</strong> button</li>
            <li>Select your fields from the list</li>
            <li>Save your rewards settings</li>
          </ol>
          
          <h2>How the POS Uses These Fields</h2>
          <ul>
            <li>When you enroll a customer in rewards, the POS checks the checkbox field</li>
            <li>The enrollment date is automatically set to today's date</li>
            <li>The Rewards page shows only customers with the checkbox enabled</li>
            <li>You can view and edit customer details from the Rewards page</li>
          </ul>
          
          <div class="tip">
            <h3>💡 Pro Tip: Automate with Your CRM</h3>
            <p>Use your CRM's automation features to create powerful workflows based on the rewards checkbox! You can automatically:</p>
            <ul>
              <li>Track total customer lifetime spend for rewards members</li>
              <li>Monitor and record last visit dates</li>
              <li>Send automated birthday or anniversary messages</li>
              <li>Create special offers for active rewards members</li>
              <li>Build segments and campaigns targeting only rewards customers</li>
              <li>Trigger follow-up sequences after purchases</li>
            </ul>
            <p><strong>How:</strong> Use the checkbox field as a filter, along with a trigger in your CRM automations. For example, set the trigger as "Payment Received" and add an "If" condition: "If Customer Rewards is Yes" - then you can execute any actions after that! Only contacts with this checkbox enabled will be rewards members, making it easy to target them with specific workflows.</p>
          </div>
          
          <script>
            window.onload = () => {
              window.print();
              // Optional: close after printing
              // window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };
  const [availableFields, setAvailableFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [loadingRewardsSettings, setLoadingRewardsSettings] = useState(true);

  // Load custom fields from CRM to find IDs
  const loadCustomFields = async () => {
    setLoadingFields(true);
    try {
      const { data } = await api.get('/live/custom-fields', { params: { live: 1 } });
      const fields = data.customFields || data || [];
      console.log('Custom fields loaded:', fields);
      
      // Filter to show only checkbox and date fields
      const relevantFields = fields.filter(f => 
        f.fieldType === 'CHECKBOX' || f.fieldType === 'DATE' || 
        f.dataType === 'CHECKBOX' || f.dataType === 'DATE'
      );
      
      setAvailableFields(relevantFields);
      setShowFieldSelector(true);
      
      toast({
        title: 'Fields Loaded',
        description: `Found ${relevantFields.length} checkbox and date fields`
      });
    } catch (error) {
      console.error('Failed to load custom fields:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load custom fields from CRM';
      
      // Check if it's an OAuth token issue
      if (error.response?.status === 401 || errorMsg.includes('reconnect') || errorMsg.includes('expired')) {
        toast({
          title: 'CRM Connection Issue',
          description: errorMsg,
          variant: 'destructive',
          action: (
            <button 
              onClick={() => window.location.href = `${BACKEND_URL}/api/auth/login?reauth=true`}
              className="px-3 py-1 bg-white text-red-700 rounded text-sm font-medium"
            >
              Reconnect Now
            </button>
          )
        });
      } else {
        toast({
          title: 'Error',
          description: errorMsg,
          variant: 'destructive'
        });
      }
    } finally {
      setLoadingFields(false);
    }
  };

  // Load available printers
  const loadPrinters = async () => {
    const available = await unifiedPrintingService.isAvailable();
    if (!available) {
      const envInfo = unifiedPrintingService.getEnvironmentInfo();
      toast({
        title: 'Printing Service Not Available',
        description: `Please ensure ${envInfo.service} is available`,
        variant: 'destructive'
      });
      return;
    }
    
    setLoadingPrinters(true);
    try {
      await unifiedPrintingService.initialize();
      const printers = await unifiedPrintingService.listPrinters();
      setAvailablePrinters(printers);
      
      if (!receiptSettings.selectedPrinter && printers.length > 0) {
        setReceiptSettings({...receiptSettings, selectedPrinter: printers[0]});
      }
      
      const envInfo = unifiedPrintingService.getEnvironmentInfo();
      toast({
        title: 'Printers Loaded',
        description: `Found ${printers.length} printer(s) via ${envInfo.service}`
      });
    } catch (error) {
      console.error('Failed to load printers:', error);
      toast({
        title: 'Error Loading Printers',
        description: error.message || 'Could not load printer list',
        variant: 'destructive'
      });
    } finally {
      setLoadingPrinters(false);
    }
  };

  // Check connection status
  const checkConnection = async () => {
    setCheckingConnection(true);
    try {
      // Check if POS user - they inherit admin's OAuth tokens
      const userType = localStorage.getItem('pos_user_type');
      if (userType === 'pos_user') {
        const storedConnection = localStorage.getItem('pos_connection_status');
        if (storedConnection) {
          setConnectionStatus(JSON.parse(storedConnection));
          setCheckingConnection(false);
          return;
        }
      }
      
      // For admin users or if no stored connection, check via API
      const { data } = await api.get('/auth/check-connection');
      setConnectionStatus(data);
    } catch (error) {
      setConnectionStatus({ connected: false, error: 'Connection check failed' });
    } finally {
      setCheckingConnection(false);
    }
    
    // Also check QZ Tray connection
    checkQzTrayConnection();
  };
  
  const checkQzTrayConnection = async () => {
    try {
      if (unifiedPrintingService.isInIframe) {
        // Inside HighLevel or any iframe, we use the Popup Printing Engine.
        // We cannot directly access QZ Tray from within the iframe due to security.
        // The service assumes connection via popup, so we mark it true here to hide the error.
        setQzConnected(true);
        return;
      }

      // If we are outside an iframe (direct access), actually check and connect via the service
      const isAvailable = await unifiedPrintingService.activeService.isAvailable();
      if (!isAvailable) {
        setQzConnected(false);
        return;
      }
      
      // Initialize handles certificates and connection via qzTrayService
      const success = await unifiedPrintingService.initialize();
      setQzConnected(Boolean(success));
      console.log('[QZ Tray] Initialization status:', success);

    } catch (error) {
      setQzConnected(false);
      console.log('[QZ Tray] Check failed:', error.message);
    }
  };

  // Print test receipt
  const printTestReceipt = async () => {
    try {
      console.log('[Test Print] Starting test print...');
      
      // Initialize printing service
      await unifiedPrintingService.initialize();
      console.log('[Test Print] Printing service initialized');
      
      // Fetch branding information
      let brandingInfo = {
        business_name: 'Your Business Name',
        business_phone: '(555) 123-4567',
        business_email: 'email@business.com'
      };
      
      try {
        const { data } = await api.get('/live/branding', { params: { live: 1 } });
        if (data) {
          brandingInfo = {
            business_name: data.businessName || data.name || 'Your Business Name',
            business_phone: data.phone || '(555) 123-4567',
            business_email: data.email || 'email@business.com'
          };
        }
      } catch (error) {
        console.log('[Test Print] Could not fetch branding, using defaults');
      }
      
      // Generate sample receipt data matching backend ReceiptRequest model
      const sampleData = {
        receipt_id: 'TEST-' + Math.random().toString(36).substring(2, 8).toUpperCase(),
        customer_name: 'Sample Customer',
        items: [
          { 
            name: 'Sample Product 1', 
            quantity: 2, 
            price: 15.99,
            total: 31.98
          },
          { 
            name: 'Sample Product 2', 
            quantity: 1, 
            price: 29.99,
            total: 29.99
          }
        ],
        subtotal: 61.97,
        discount: 0.0,
        tax: 4.94,
        total: 66.91,
        payment_method: 'Cash',
        cash_tendered: 70.00,
        change_due: 3.09,
        settings: {
          showLogo: receiptSettings.showLogo,
          showPhone: receiptSettings.showPhone,
          showEmail: receiptSettings.showEmail,
          showBarcode: receiptSettings.showBarcode,
          topMargin: 0,  // Printer handles top spacing
          bottomMargin: 0,  // Printer handles bottom spacing
          leftMargin: receiptSettings.leftMargin || 5,
          rightMargin: receiptSettings.rightMargin || 5,
          sideMargin: receiptSettings.sideMargin || 5,
          thankYouMessage: receiptSettings.thankYouMessage || 'Thank You!\nPlease come again'
        }
      };
      
      console.log('[Test Print] Generating receipt HTML...');
      const { data } = await api.post('/receipt/generate', sampleData);
      
      if (!data.success) {
        throw new Error('Failed to generate test receipt');
      }
      
      console.log('[Test Print] Receipt generated, length:', data.receipt_html?.length);
      
      // Prepare margins (only left/right - printer handles top/bottom)
      const margins = {
        top: 0,  // Printer handles top spacing
        left: receiptSettings.leftMargin || receiptSettings.sideMargin || 0,
        right: receiptSettings.rightMargin || receiptSettings.sideMargin || 0,
        bottom: 0  // Printer handles bottom spacing
      };
      
      console.log('[Test Print] Using margins:', margins);
      
      // Print receipt
      await unifiedPrintingService.printReceipt(data.receipt_html, {
        printerName: receiptSettings.selectedPrinter || null,
        businessName: sampleData.business_name,
        footer: receiptSettings.thankYouMessage || 'Thank you for your business!',
        margins
      });
      
      console.log('[Test Print] Test receipt printed successfully');
      
      toast({
        title: 'Test Receipt Printed',
        description: 'Check your printer for the test receipt'
      });
    } catch (error) {
      console.error('[Test Print] Error:', error);
      toast({
        title: 'Print Failed',
        description: error.message || 'Failed to print test receipt',
        variant: 'destructive'
      });
    }
  };

  // Check Stripe connection status
  const checkStripeStatus = async () => {
    console.log('[Settings] checkStripeStatus called. connectionStatus:', connectionStatus);
    
    // Don't check Stripe if CRM is not connected (no location_id means no context)
    if (!connectionStatus?.connected) {
      console.log('[Settings] Skipping Stripe check - CRM not connected');
      setStripeStatus({ connected: false, error: 'CRM not connected' });
      setCheckingStripe(false);
      return;
    }
    
    console.log('[Settings] CRM is connected, checking Stripe status...');
    setCheckingStripe(true);
    try {
      const { data } = await api.get('/stripe/status');
      console.log('[Settings] Stripe status response:', data);
      setStripeStatus(data);
    } catch (error) {
      console.error('[Settings] Stripe status check failed:', error);
      setStripeStatus({ connected: false, error: 'Failed to check Stripe status' });
    } finally {
      setCheckingStripe(false);
    }
  };

  // Connect to Stripe
  const connectStripe = () => {
    // Direct navigation to backend endpoint which will redirect to Stripe
    window.location.href = `${BACKEND_URL}/api/stripe/connect`;
  };

  // Disconnect from Stripe
  const disconnectStripe = async () => {
    try {
      await api.post('/stripe/disconnect');
      toast({
        title: 'Stripe Disconnected',
        description: 'Your Stripe account has been disconnected'
      });
      checkStripeStatus();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect Stripe account',
        variant: 'destructive'
      });
    }
  };

  // Check user type
  const isAdmin = () => {
    // Check if user is admin based on GHL role or POS admin status
    if (userInfo?.user_type === 'pos_user') return false;
    if (userInfo?.user_type === 'oauth') {
      // New: check is_admin field from GHL role
      return userInfo?.is_admin !== false; // Default to true for backward compatibility
    }
    return false;
  };
  
  const isPOSUser = () => userInfo?.user_type === 'pos_user';
  
  // Check if running on Android (Capacitor)
  const isAndroid = typeof window !== 'undefined' && 
    (window.Capacitor?.isNativePlatform?.() || 
     window.Capacitor?.getPlatform?.() === 'android' ||
     /android/i.test(navigator.userAgent));
  
  // Check QZ Tray availability (only relevant on desktop)
  const qzTrayAvailable = !isAndroid && typeof window !== 'undefined' && !!window.qz;

  // Fetch current user info
  useEffect(() => {
    // CRITICAL: Capture session_id from URL if present (from OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = urlParams.get('session_id');
    if (sessionIdFromUrl) {
      console.log('[Settings] 🔑 Found session_id in URL, storing in localStorage:', sessionIdFromUrl.substring(0, 20) + '...');
      localStorage.setItem('pos_session_id', sessionIdFromUrl);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    const fetchUserInfo = async () => {
      try {
        const { data } = await api.get('/auth/me');
        setUserInfo(data);
        console.log('User info:', data);
        
        // Load display settings from backend (works for both POS and admin users)
        if (data.display_settings) {
          console.log('[Settings] Loading display settings from backend:', data.display_settings);
          // Convert snake_case backend format to camelCase frontend format
          const loadedSettings = {
            columns: data.display_settings.columns || 4,
            showImages: data.display_settings.show_images !== undefined ? data.display_settings.show_images : true,
            viewMode: data.display_settings.view_mode || 'grid'
          };
          setDisplaySettings(loadedSettings);
          
          // Also update localStorage for immediate effect on other pages
          localStorage.setItem('display_settings', JSON.stringify(loadedSettings));
        } else {
          // Fallback to localStorage if backend doesn't have settings yet
          const s = localStorage.getItem('display_settings');
          if (s) {
            const saved = JSON.parse(s);
            console.log('[Settings] Loading display settings from localStorage (fallback):', saved);
            setDisplaySettings(saved);
          } else {
            console.log('[Settings] No display settings found, using defaults');
          }
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUserInfo();
  }, []);

  // Debug: Log user info when it changes
  useEffect(() => {
    if (userInfo) {
      console.log('🔍 [Settings] Current user info:', {
        user_type: userInfo.user_type,
        user_role: userInfo.user_role,
        is_admin: userInfo.is_admin,
        user_email: userInfo.user_email,
        user_name: userInfo.user_name
      });
      console.log('🔍 [Settings] isAdmin() returns:', isAdmin());
    }
  }, [userInfo]);

  // Load rewards settings from backend
  useEffect(() => {
    const loadRewardsSettings = async () => {
      try {
        console.log('[Settings] Loading rewards settings from backend...');
        const { data } = await api.get('/settings/rewards');
        console.log('[Settings] Rewards settings loaded:', data);
        setRewardsSettings(data);
        // Also update localStorage for backward compatibility
        localStorage.setItem('rewards_settings', JSON.stringify(data));
      } catch (error) {
        console.error('[Settings] Failed to load rewards settings:', error);
        // Fall back to localStorage if backend fails
        const s = localStorage.getItem('rewards_settings');
        if (s) {
          console.log('[Settings] Using localStorage fallback for rewards settings');
          setRewardsSettings(JSON.parse(s));
        }
      } finally {
        setLoadingRewardsSettings(false);
      }
    };
    loadRewardsSettings();
  }, []);


  // Load admin settings (receipt preferences + tax rate) from backend
  useEffect(() => {
    const loadAdminSettings = async () => {
      try {
        console.log('[Settings] Loading admin settings from backend...');
        const { data } = await api.get('/settings/admin');
        console.log('[Settings] Admin settings loaded:', data);
        
        // Update tax rate
        if (data.tax_rate !== undefined) {
          setTaxRate(data.tax_rate);
        }
        
        // Update CC fee settings
        if (data.cc_fee_enabled !== undefined) {
          setCcFeeEnabled(data.cc_fee_enabled);
        }
        if (data.cc_fee_rate !== undefined) {
          setCcFeeRate(data.cc_fee_rate * 100); // Convert decimal to percentage for display
        }
        // Persist to localStorage for InvoicePayment to read
        localStorage.setItem('cc_fee_settings', JSON.stringify({
          enabled: data.cc_fee_enabled || false,
          rate: data.cc_fee_rate || 0.03
        }));
        
        // Update receipt settings with admin preferences
        if (data.receipt_settings) {
          setReceiptSettings(prev => ({
            ...prev,
            showLogo: data.receipt_settings.show_logo,
            showPhone: data.receipt_settings.show_phone,
            showEmail: data.receipt_settings.show_email,
            showBarcode: data.receipt_settings.show_barcode,
            thankYouMessage: data.receipt_settings.thank_you_message,
            allowCompleteWithoutPrinting: data.receipt_settings.allow_complete_without_printing || false,
            selectedPrinter: data.last_selected_printer || prev.selectedPrinter
          }));
        }
        
        setAdminSettingsLoaded(true);
      } catch (error) {
        console.error('[Settings] Failed to load admin settings:', error);
        // Use defaults on error
        setAdminSettingsLoaded(true);
      }
    };
    loadAdminSettings();
  }, []);

  // Load device settings when a printer is selected (ONE TIME ONLY)
  const [deviceSettingsLoadedForPrinter, setDeviceSettingsLoadedForPrinter] = useState('');
  
  useEffect(() => {
    const loadDeviceSettings = async () => {
      if (!receiptSettings.selectedPrinter || !adminSettingsLoaded) return;
      
      // Only load device settings ONCE per printer selection to avoid overwriting user edits
      if (deviceSettingsLoadedForPrinter === receiptSettings.selectedPrinter) {
        console.log(`[Settings] Device settings already loaded for: ${receiptSettings.selectedPrinter}, skipping reload`);
        return;
      }
      
      try {
        console.log(`[Settings] Loading device settings for printer: ${receiptSettings.selectedPrinter}`);
        const { data } = await api.get(`/settings/device/${encodeURIComponent(receiptSettings.selectedPrinter)}`);
        console.log('[Settings] Device settings loaded:', data);
        
        // Update receipt settings with device-specific settings
        setReceiptSettings(prev => ({
          ...prev,
          leftMargin: data.left_margin,
          rightMargin: data.right_margin,
          openCashDrawer: data.open_cash_drawer
        }));
        
        // Mark this printer's settings as loaded
        setDeviceSettingsLoadedForPrinter(receiptSettings.selectedPrinter);
      } catch (error) {
        console.error('[Settings] Failed to load device settings:', error);
        // Mark as loaded anyway to prevent retry loop
        setDeviceSettingsLoadedForPrinter(receiptSettings.selectedPrinter);
      }
    };
    loadDeviceSettings();
  }, [receiptSettings.selectedPrinter, adminSettingsLoaded]);


  // Load branding and check connections on mount
  useEffect(() => {
    checkConnection();
    
    // Check for Stripe OAuth callback parameters
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_connected') === 'true') {
      toast({
        title: 'Stripe Connected!',
        description: 'Your Stripe account has been successfully connected'
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      checkStripeStatus();
    } else if (params.get('stripe_error')) {
      toast({
        title: 'Stripe Connection Failed',
        description: params.get('stripe_error'),
        variant: 'destructive'
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    api.get('/live/branding', { params: { live: 1 } })
      .then((r)=>setBrand(r.data))
      .catch(()=>{});
  }, []);
  // Watch connectionStatus and check Stripe when CRM is connected
  useEffect(() => {
    if (connectionStatus?.connected) {
      checkStripeStatus();
    }
  }, [connectionStatus]);

  // Persist device settings
  const save = () => {
    const payload = { printerName, registerId, cashDrawer };
    localStorage.setItem('pos_settings', JSON.stringify(payload));
    toast({ title: 'Success', description: 'Device settings saved!' });
  };

  const reconnectToGHL = async () => {
    try {
      console.log('[Reconnect] Using GHL Reconnect API...');
      setReconnecting(true);
      
      // Use GHL's Reconnect API (no user interaction needed)
      const { data } = await api.post('/auth/reconnect');
      
      console.log('[Reconnect] ✅ Success:', data);
      
      toast({
        title: 'Connection Restored',
        description: 'Successfully reconnected to GoHighLevel CRM',
        variant: 'success'
      });
      
      // Refresh connection status
      await checkConnection();
      setReconnecting(false);
      
    } catch (error) {
      console.error('[Reconnect] Failed:', error);
      setReconnecting(false);
      
      // If Reconnect API fails, fall back to full OAuth in a POPUP window
      // (OAuth pages don't work inside iframes due to GHL's CSP/X-Frame-Options)
      const status = error?.response?.status;
      if (status === 400 || status === 404 || status === 401) {
        console.log('[Reconnect] Reconnect API failed, opening OAuth in popup...');
        toast({
          title: 'Authorization Required',
          description: 'Opening authorization window...',
          variant: 'warning'
        });
        
        // Open OAuth in a popup window (works around iframe restrictions)
        const authUrl = `${BACKEND_URL}/api/auth/login?reauth=true`;
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          authUrl,
          'ghl_oauth_popup',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
        );
        
        // Listen for OAuth success message from popup
        const handleOAuthMessage = async (event) => {
          if (event.data?.type === 'OAUTH_SUCCESS') {
            console.log('[Reconnect] Received OAuth success message from popup');
            window.removeEventListener('message', handleOAuthMessage);
            clearInterval(checkPopup);
            // Connection should be ready now - check and reload
            await checkConnection();
            window.location.reload();
          }
        };
        window.addEventListener('message', handleOAuthMessage);
        
        // Fallback: Listen for popup close (in case message fails)
        const checkPopup = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(checkPopup);
            window.removeEventListener('message', handleOAuthMessage);
            console.log('[Reconnect] Popup closed, checking connection...');
            // Check if connection was restored
            setTimeout(async () => {
              await checkConnection();
              // Reload to get fresh session data
              window.location.reload();
            }, 500);
          }
        }, 500);
        
      } else {
        toast({
          title: 'Reconnection Error',
          description: error?.response?.data?.detail || 'Failed to restore connection',
          variant: 'destructive'
        });
      }
    }
  };

  if (loadingUser) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* User Info Banner */}
      {userInfo && (
        <div className={`${isAdmin() ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isAdmin() ? 'text-blue-900' : 'text-gray-900'}`}>
                {isPOSUser() ? `👤 ${userInfo.display_name || userInfo.username}` : (isAdmin() ? '👔 Admin Access' : `👤 ${userInfo.user_name || userInfo.user_email}`)}
              </p>
              <p className={`text-sm ${isAdmin() ? 'text-blue-700' : 'text-gray-700'}`}>
                {isPOSUser() ? 'POS Employee' : (isAdmin() ? 'Full Administrator' : 'Staff Member')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Users Management - Admin Only */}
      {/* POS User Management removed - using GHL user management */}

      {/* Stripe Integration Card - Admin Only (POS users can view but not modify) */}
      <Card className={!connectionStatus?.connected ? 'opacity-60' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Stripe Payment Processing
            {!connectionStatus?.connected && (
              <span className="text-xs font-normal text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                Requires CRM Connection
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Show CRM connection warning if not connected */}
          {!connectionStatus?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-gray-100 border border-gray-300 rounded-lg">
                <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
                <span className="text-sm font-medium text-gray-600">Unavailable</span>
              </div>
              <div className="space-y-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm font-semibold text-yellow-900">Connect CRM First</span>
                </div>
                <p className="text-sm text-yellow-800">
                  Stripe integration requires an active CRM connection to determine which location's payment settings to use. Please connect your CRM in the section above before configuring Stripe.
                </p>
              </div>
              <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-500">
                  <div className="font-medium text-gray-700">Account:</div>
                  <div className="text-gray-400">—</div>
                </div>
                <div className="text-sm text-gray-500">
                  <div className="font-medium text-gray-700">Email:</div>
                  <div className="text-gray-400">—</div>
                </div>
                <div className="text-sm text-gray-500">
                  <div className="font-medium text-gray-700">Currency:</div>
                  <div className="text-gray-400">—</div>
                </div>
              </div>
            </div>
          ) : checkingStripe ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              Checking Stripe connection...
            </div>
          ) : stripeStatus?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                <span className="text-sm font-medium text-green-900">Stripe Connected</span>
              </div>
              
              {stripeStatus.account_name && (
                <div className="text-sm">
                  <div className="font-semibold text-gray-700">Account:</div>
                  <div className="text-gray-600">{stripeStatus.account_name}</div>
                </div>
              )}
              
              {stripeStatus.email && (
                <div className="text-sm">
                  <div className="font-semibold text-gray-700">Email:</div>
                  <div className="text-gray-600">{stripeStatus.email}</div>
                </div>
              )}
              
              {stripeStatus.currency && (
                <div className="text-sm">
                  <div className="font-semibold text-gray-700">Currency:</div>
                  <div className="text-gray-600">{stripeStatus.currency}</div>
                </div>
              )}
              
              {isAdmin() && (
                <Button 
                  onClick={disconnectStripe}
                  variant="outline"
                  className="w-full"
                >
                  Disconnect Stripe
                </Button>
              )}
              {isPOSUser() && (
                <p className="text-xs text-gray-500 italic">
                  Contact an administrator to modify Stripe connection
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
                <span className="text-sm font-medium text-gray-900">Not Connected</span>
              </div>
              <p className="text-sm text-gray-600">
                Connect your Stripe account to process card payments directly through your POS.
              </p>
              
              {/* Check if CRM is connected before allowing Stripe connection */}
              {!connectionStatus?.connected ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-sm font-medium text-yellow-900">CRM must be connected first</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Stripe connection requires an active CRM connection. Please connect to your CRM above before connecting Stripe.
                  </p>
                </div>
              ) : isAdmin() ? (
                <a 
                  href={`${BACKEND_URL}/api/stripe/connect?session_id=${localStorage.getItem('pos_session_id')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button 
                    className="w-full h-12 text-lg px-6 bg-indigo-600 hover:bg-indigo-700"
                  >
                    Connect with Stripe
                  </Button>
                </a>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  Contact an administrator to connect Stripe
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CRM Connection Card */}
      <Card>
        <CardHeader><CardTitle>CRM Connection</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Connection Status Indicator */}
          {checkingConnection ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              Checking connection...
            </div>
          ) : connectionStatus?.connected ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="h-3 w-3 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-green-900">No problems detected - Connection active</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="h-3 w-3 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium text-red-900">
                Connection lost - {connectionStatus?.error || 'Please reconnect'}
              </span>
            </div>
          )}

          <p className="text-sm text-gray-600">
            If you're experiencing connection issues with your CRM, {isAdmin() ? 'click the button below to reconnect your account' : 'contact an administrator to reconnect'}.
          </p>
          {isAdmin() && (
            <Button 
              onClick={reconnectToGHL}
              disabled={reconnecting}
              className="h-12 text-lg px-6 bg-blue-600 hover:bg-blue-700"
            >
              {reconnecting ? 'Reconnecting...' : 'Reconnect to CRM'}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Tax Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm opacity-70 mb-2 block">Tax Rate (decimal format)</label>
            <Input 
              data-testid="tax-rate-input" 
              className="h-12 text-lg" 
              type="number" 
              step="0.001" 
              placeholder="e.g. 0.075 for 7.5%"
              value={taxRate} 
              onChange={(e)=>setTaxRate(e.target.value)}
              disabled={!isAdmin()}
            />
            <p className="text-xs opacity-60 mt-2">Example: 0.075 = 7.5% tax rate</p>
            {!isAdmin() && (
              <p className="text-xs text-gray-500 italic mt-2">
                Contact an administrator to modify tax settings
              </p>
            )}
          </div>
          {isAdmin() && (
            <Button className="h-12 text-lg px-6" onClick={async ()=>{
              try {
                // Save to backend
                await api.put('/settings/admin', { tax_rate: Number(taxRate || 0) });
                
                // Also update localStorage for immediate effect
                localStorage.setItem('pos_settings', JSON.stringify({ ...JSON.parse(localStorage.getItem('pos_settings')||'{}'), taxRate: Number(taxRate||0) }));
                
                // Dispatch custom event so CartContext picks up the change in same window
                window.dispatchEvent(new Event('taxRateUpdated'));
                
                toast({ title: 'Success', description: 'Tax rate saved successfully!' });
              } catch (error) {
                console.error('Failed to save tax rate:', error);
                toast({ title: 'Error', description: 'Failed to save tax rate', variant: 'destructive' });
              }
            }}>Save Tax Rate</Button>
          )}
        </CardContent>
      </Card>

      {/* Credit Card Fee Settings - Admin Only */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Credit Card Fee
            {ccFeeEnabled && (
              <span className="text-xs font-normal text-green-700 bg-green-50 px-2 py-1 rounded">
                Active — {ccFeeRate}%
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Pass Credit Card Fee to Customer</div>
              <div className="text-sm text-gray-600">
                Automatically add a processing fee to card payments
              </div>
            </div>
            <Switch
              checked={ccFeeEnabled}
              onCheckedChange={(checked) => setCcFeeEnabled(checked)}
              disabled={!isAdmin()}
            />
          </div>

          {ccFeeEnabled && (
            <div className="space-y-3 pt-2 border-t">
              <div>
                <label className="text-sm opacity-70 mb-2 block">Fee Percentage (%)</label>
                <Input
                  className="h-12 text-lg"
                  type="number"
                  step="0.1"
                  min="0"
                  max="4"
                  placeholder="e.g. 3.0 for 3%"
                  value={ccFeeRate}
                  onChange={(e) => setCcFeeRate(e.target.value)}
                  disabled={!isAdmin()}
                />
                <p className="text-xs opacity-60 mt-2">
                  Example: 3.0 = 3% fee added to card payments only. Cash and check payments are not affected.
                </p>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="text-xs text-amber-800">
                    <strong>Legal Notice:</strong> Credit card surcharges are subject to state and local regulations.
                    Surcharges are generally capped at 4% and must be disclosed to customers before payment.
                    Please verify compliance with your local laws before enabling this feature.
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isAdmin() && (
            <p className="text-xs text-gray-500 italic">
              Contact an administrator to modify credit card fee settings
            </p>
          )}

          {isAdmin() && (
            <Button className="h-12 text-lg px-6" onClick={async () => {
              try {
                await api.put('/settings/admin', {
                  cc_fee_enabled: ccFeeEnabled,
                  cc_fee_rate: Number(ccFeeRate || 0) / 100 // Convert percentage to decimal
                });

                // Persist to localStorage for immediate use
                localStorage.setItem('cc_fee_settings', JSON.stringify({
                  enabled: ccFeeEnabled,
                  rate: Number(ccFeeRate || 0) / 100
                }));

                toast({ title: 'Success', description: ccFeeEnabled ? `Credit card fee set to ${ccFeeRate}%` : 'Credit card fee disabled' });
              } catch (error) {
                console.error('Failed to save CC fee settings:', error);
                toast({ title: 'Error', description: 'Failed to save credit card fee settings', variant: 'destructive' });
              }
            }}>Save CC Fee Settings</Button>
          )}
        </CardContent>
      </Card>

      {/* Product Display Settings */}
      <Card>
        <CardHeader><CardTitle>Product Display Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="font-medium">Number of Columns</label>
                <span className="text-sm text-gray-600">{displaySettings.columns}</span>
              </div>
              <input 
                type="range"
                min="2"
                max="10"
                step="1"
                value={displaySettings.columns}
                onChange={(e) => setDisplaySettings({...displaySettings, columns: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <p className="text-xs text-gray-500">Adjust how many products appear per row (2-10)</p>
            </div>
            
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <div className="font-medium">Show Product Images</div>
                <div className="text-sm text-gray-600">Display product images on sale page</div>
              </div>
              <Switch 
                checked={displaySettings.showImages}
                onCheckedChange={(checked) => setDisplaySettings({...displaySettings, showImages: checked})}
              />
            </div>
            
            <div className="space-y-2 pt-2 border-t">
              <label className="font-medium">View Mode</label>
              <div className="flex gap-2">
                <Button 
                  variant={displaySettings.viewMode === 'grid' ? 'default' : 'outline'}
                  onClick={() => setDisplaySettings({...displaySettings, viewMode: 'grid'})}
                  className="flex-1"
                >
                  Grid View
                </Button>
                <Button 
                  variant={displaySettings.viewMode === 'list' ? 'default' : 'outline'}
                  onClick={() => setDisplaySettings({...displaySettings, viewMode: 'list'})}
                  className="flex-1"
                >
                  List View
                </Button>
              </div>
              <p className="text-xs text-gray-500">Choose between grid or compact list layout</p>
            </div>
          </div>
          
          <Button 
            className="w-full h-12 text-lg"
            onClick={async () => {
              // Save to backend API for both POS users and admin users
              try {
                await api.put('/users/me/settings', {
                  view_mode: displaySettings.viewMode,
                  columns: displaySettings.columns,
                  show_images: displaySettings.showImages
                });
                
                // Also update localStorage for immediate effect
                localStorage.setItem('display_settings', JSON.stringify(displaySettings));
                
                toast({ 
                  title: 'Success', 
                  description: 'Your display settings have been saved!' 
                });
              } catch (error) {
                console.error('Failed to save settings:', error);
                toast({ 
                  title: 'Error', 
                  description: error.response?.data?.detail || 'Failed to save settings',
                  variant: 'destructive'
                });
              }
            }}
          >
            Save Display Settings
          </Button>
        </CardContent>
      </Card>

      {/* Printer Settings - For All Users (printer selection, margins, test print) */}
      {(isPOSUser() || !isAdmin()) && (
      <Card>
        <CardHeader>
          <CardTitle>Printer Settings</CardTitle>
          <p className="text-sm text-gray-600">Configure your receipt printer and cash drawer</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Platform Detection - Show different UI for Android vs Desktop */}
          {isAndroid ? (
            <>
              {/* Native Android Thermal Printer Status */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">Native Thermal Printer</h3>
                <div className="space-y-2 mt-3">
                  <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-green-900">Native printing enabled via Capacitor</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    This app uses native Android thermal printing. Connect your Bluetooth or USB thermal printer to get started.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* QZ Tray Status - Desktop Only */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">QZ Tray Status</h3>
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${qzTrayAvailable ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm">
                    {qzTrayAvailable ? 'QZ Tray Connected' : 'QZ Tray Not Connected'}
                  </span>
                </div>
                {!qzTrayAvailable && (
                  <p className="text-xs text-gray-500 mt-2">
                    Install QZ Tray desktop application to enable printing. Download from{' '}
                    <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      qz.io/download
                    </a>
                  </p>
                )}
              </div>
            </>
          )}

          {/* Printer Selection - Works on both Android and Desktop */}
          <div className="space-y-2">
            <label className="font-medium text-sm">Select Printer</label>
            <select
              value={receiptSettings.selectedPrinter}
              onChange={(e) => {
                const newSettings = { ...receiptSettings, selectedPrinter: e.target.value };
                setReceiptSettings(newSettings);
                localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
              }}
              className="w-full p-2 border rounded-md"
              disabled={!isAndroid && !qzTrayAvailable}
            >
              <option value="">Select a printer...</option>
              {availablePrinters.map((printer) => (
                <option key={printer} value={printer}>{printer}</option>
              ))}
            </select>
            {(qzTrayAvailable || isAndroid) && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadPrinters}
                >
                  Refresh Printers
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await unifiedPrintingService.initialize();
                      const testHTML = `
                        <div style="text-align: center;">
                          <h1>TEST PRINT</h1>
                          <p>Castle POS</p>
                          <p>Printer: ${receiptSettings.selectedPrinter || 'Default'}</p>
                          <p>Time: ${new Date().toLocaleString()}</p>
                        </div>
                      `;
                      await unifiedPrintingService.printReceipt(testHTML, { printerName: receiptSettings.selectedPrinter });
                      toast({
                        title: 'Test Print Sent',
                        description: 'Check your printer for test receipt'
                      });
                    } catch (error) {
                      toast({
                        title: 'Test Print Failed',
                        description: error.message,
                        variant: 'destructive'
                      });
                    }
                  }}
                  disabled={!receiptSettings.selectedPrinter}
                >
                  Test Print
                </Button>
              </div>
            )}
          </div>

          {/* Cash Drawer Toggle and Test */}
          {(qzTrayAvailable || isAndroid) && (
            <>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <label className="font-medium text-sm">Open Cash Drawer Automatically</label>
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically open cash drawer for cash and check payments
                  </p>
                </div>
                <Switch
                  checked={receiptSettings.openCashDrawer}
                  onCheckedChange={(checked) => {
                    const newSettings = { ...receiptSettings, openCashDrawer: checked };
                    setReceiptSettings(newSettings);
                    localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                  }}
                />
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await unifiedPrintingService.initialize();
                    await unifiedPrintingService.openCashDrawer(receiptSettings.selectedPrinter);
                    toast({
                      title: 'Cash Drawer Pulse Sent',
                      description: 'Drawer should open if connected'
                    });
                  } catch (error) {
                    toast({
                      title: 'Drawer Pulse Failed',
                      description: error.message,
                      variant: 'destructive'
                    });
                  }
                }}
                disabled={!receiptSettings.selectedPrinter}
              >
                Test Cash Drawer
              </Button>
            </>
          )}

          <p className="text-xs text-gray-500 italic">
            {isAndroid 
              ? 'Works with USB or Bluetooth printers connected to this Android device'
              : 'Printer settings are saved locally on this device'}
          </p>
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader><CardTitle>Branding</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600 italic">
            Branding is automatically pulled from your CRM account
          </p>
          {brand && (
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border">
              {brand.logo_url && <img alt="logo" src={brand.logo_url} className="h-16" data-testid="brand-logo"/>}
              <div>
                <div className="font-semibold text-lg" data-testid="brand-name">{brand.business_name}</div>
                <div className="text-sm opacity-70">{brand.phone} • {brand.email}</div>
              </div>
            </div>
          )}
          {!brand && (
            <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg border">
              Loading branding information...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stripe Terminal Reader - Admin Only */}
      {isAdmin() && (
      <>
      <Card>
        <CardHeader>
          <CardTitle>Stripe Terminal Reader</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Connect a Stripe Terminal card reader for in-person card payments
          </p>
          
          {connectedReader ? (
            <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-lg text-green-800 flex items-center gap-2">
                    <Wifi className="h-5 w-5" />
                    Reader Connected
                  </div>
                  <div className="text-sm text-green-700 mt-1">
                    {connectedReader.label || connectedReader.serial_number || 'Stripe Reader'}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {connectedReader.device_type || 'Unknown model'}
                  </div>
                  {readerStatus?.batteryLevel && (
                    <div className="text-xs text-green-600 mt-1">
                      Battery: {readerStatus.batteryLevel}%
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const status = await stripeTerminal.getReaderStatus();
                        setReaderStatus(status);
                        toast({
                          title: 'Reader Status',
                          description: status.connected ? 'Reader is connected and ready' : 'Reader disconnected'
                        });
                      } catch (error) {
                        toast({
                          title: 'Status Check Failed',
                          description: error.message,
                          variant: 'destructive'
                        });
                      }
                    }}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await stripeTerminal.disconnectReader();
                        localStorage.removeItem('stripe_terminal_reader');
                        setConnectedReader(null);
                        setReaderStatus(null);
                        toast({
                          title: 'Reader Disconnected',
                          description: 'Stripe reader has been disconnected'
                        });
                      } catch (error) {
                        toast({
                          title: 'Disconnect Failed',
                          description: error.message,
                          variant: 'destructive'
                        });
                      }
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 border rounded-lg">
                <div className="font-semibold text-gray-700">No Reader Connected</div>
                <div className="text-sm text-gray-600 mt-1">
                  Discover and connect to your Stripe Terminal reader
                </div>
              </div>
              
              <Button
                variant="default"
                onClick={async () => {
                  setDiscoveringReaders(true);
                  try {
                    // First, get the Stripe location ID from the connection
                    let stripeLocationId = null;
                    try {
                      const { data } = await api.get('/stripe/status');
                      stripeLocationId = data.location_id;
                      console.log('[Reader Discovery] Using Stripe location:', stripeLocationId);
                    } catch (error) {
                      console.warn('[Reader Discovery] Could not fetch Stripe location:', error);
                    }
                    
                    // Discover readers with location (required for real readers)
                    const readers = await stripeTerminal.discoverReaders({ 
                      simulated: false,
                      location: stripeLocationId 
                    });
                    setDiscoveredReaders(readers);
                    setShowReaderDialog(true);
                    
                    if (readers.length === 0) {
                      toast({
                        title: 'No Readers Found',
                        description: stripeLocationId 
                          ? 'Make sure your reader is powered on and registered to this Stripe location'
                          : 'No Stripe location found. Please register readers in Stripe Dashboard',
                        variant: 'destructive'
                      });
                    }
                  } catch (error) {
                    console.error('Discovery error:', error);
                    toast({
                      title: 'Discovery Failed',
                      description: error.message || 'Could not discover readers',
                      variant: 'destructive'
                    });
                  } finally {
                    setDiscoveringReaders(false);
                  }
                }}
                disabled={discoveringReaders}
                className="h-12 w-full"
              >
                {discoveringReaders ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Discovering...
                  </>
                ) : (
                  <>
                    <Wifi className="h-4 w-4 mr-2" />
                    Discover Readers
                  </>
                )}
              </Button>
              
              <div className="text-sm text-gray-600 space-y-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="font-semibold text-blue-800">Setup Instructions:</div>
                <ol className="list-decimal ml-4 space-y-1 text-blue-700 text-xs">
                  <li>Register your reader in Stripe Dashboard (Terminal → Readers → Register Reader)</li>
                  <li>Assign reader to a Stripe Terminal Location</li>
                  <li>Power on your reader and ensure it's connected to WiFi/Internet</li>
                  <li>Click "Discover Readers" below to find your reader</li>
                  <li>Select your reader from the list to connect</li>
                </ol>
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> Simulated readers are not available with connected Stripe accounts. 
                    Real hardware (WisePOS E, Verifone P400) is required.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Reader Selection Dialog */}
      <Dialog open={showReaderDialog} onOpenChange={setShowReaderDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Available Stripe Terminal Readers</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {discoveredReaders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No readers found</p>
                <p className="text-sm mt-2">Make sure your reader is powered on and in range</p>
              </div>
            ) : (
              discoveredReaders.map((reader) => (
                <div
                  key={reader.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={async () => {
                    setConnectingReader(true);
                    try {
                      console.log('[Settings] Starting connection to reader:', reader);
                      const connected = await stripeTerminal.connectReader(reader);
                      console.log('[Settings] Connection successful:', connected);
                      
                      setConnectedReader({
                        id: connected.id,
                        label: connected.label,
                        serial_number: connected.serial_number,
                        device_type: connected.device_type,
                        status: connected.status
                      });
                      setShowReaderDialog(false);
                      
                      toast({
                        title: 'Reader Connected!',
                        description: `Connected to ${connected.label || connected.serial_number}`
                      });
                    } catch (error) {
                      console.error('[Settings] Connection failed:', error);
                      console.error('[Settings] Error details:', {
                        message: error.message,
                        stack: error.stack,
                        error: error
                      });
                      
                      toast({
                        title: 'Connection Failed',
                        description: error.message || 'Unknown error occurred',
                        variant: 'destructive'
                      });
                    } finally {
                      setConnectingReader(false);
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">
                        {reader.label || reader.serial_number}
                      </div>
                      <div className="text-sm text-gray-600">
                        {reader.device_type} • {reader.status}
                      </div>
                      {reader.serial_number && (
                        <div className="text-xs text-gray-500">
                          S/N: {reader.serial_number}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      disabled={connectingReader}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {connectingReader ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Connect'
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}
      
      {/* Card Payment Mode Setting - Admin Only */}
      {isAdmin() && (
      <Card>
        <CardHeader>
          <CardTitle>Card Payment Mode</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Choose how card payments should work when customers click "Card" at checkout
          </p>
          
          <div className="space-y-3">
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                cardPaymentMode === 'reader_first' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => {
                setCardPaymentMode('reader_first');
                localStorage.setItem('card_payment_mode', 'reader_first');
                toast({
                  title: 'Payment Mode Updated',
                  description: 'Card payments will use Terminal reader first, with manual entry as fallback'
                });
              }}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  cardPaymentMode === 'reader_first' ? 'border-blue-500' : 'border-gray-300'
                }`}>
                  {cardPaymentMode === 'reader_first' && (
                    <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-base">Terminal Reader First (Recommended)</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Try to connect to Terminal reader automatically. If no reader is found or connection fails, 
                    show manual card entry as fallback option. Best for stores with card readers.
                  </div>
                </div>
              </div>
            </div>
            
            <div 
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                cardPaymentMode === 'manual_entry' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => {
                setCardPaymentMode('manual_entry');
                localStorage.setItem('card_payment_mode', 'manual_entry');
                toast({
                  title: 'Payment Mode Updated',
                  description: 'Card payments will use manual entry by default'
                });
              }}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  cardPaymentMode === 'manual_entry' ? 'border-blue-500' : 'border-gray-300'
                }`}>
                  {cardPaymentMode === 'manual_entry' && (
                    <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-base">Manual Entry (Classic)</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Always show manual card entry form. Customer types card details manually. 
                    Good for phone/mail orders or if you don't have a card reader.
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-xs text-gray-500 pt-2 border-t">
            This setting affects all users at this location
          </div>
        </CardContent>
      </Card>
      )}

      {/* Customer Rewards Settings - Admin Only */}
      {isAdmin() && (
      <>
      <Card>
        <CardHeader>
          <CardTitle>Customer Rewards Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2">
            <div>
              <div className="font-semibold text-base">Enable Rewards System</div>
              <div className="text-sm text-gray-600">Turn on customer rewards throughout the POS</div>
            </div>
            <Switch 
              checked={rewardsSettings.enabled}
              onCheckedChange={(checked) => setRewardsSettings({...rewardsSettings, enabled: checked})}
            />
          </div>
          
          <p className="text-sm text-gray-600">
            Configure your CRM custom fields for the customer rewards program
          </p>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowRewardsInstructions(true)}
              disabled={!rewardsSettings.enabled}
            >
              📋 Setup Instructions
            </Button>
            <Button
              variant="default"
              className="flex-1"
              onClick={loadCustomFields}
              disabled={!rewardsSettings.enabled || loadingFields}
            >
              {loadingFields ? 'Loading...' : '🔍 Find Field IDs'}
            </Button>
          </div>
          
          <div className={`space-y-3 pt-3 border-t ${!rewardsSettings.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="space-y-2">
              <label className="font-medium text-sm">Checkbox Field</label>
              <Input
                placeholder="Field ID"
                value={rewardsSettings.checkboxFieldId}
                onChange={(e) => setRewardsSettings({...rewardsSettings, checkboxFieldId: e.target.value})}
                className="font-mono text-xs"
              />
              {rewardsSettings.checkboxFieldName && (
                <p className="text-xs text-green-600">✓ {rewardsSettings.checkboxFieldName}</p>
              )}
              <p className="text-xs text-gray-500">The field ID from your CRM (use "Find Field IDs" button)</p>
            </div>
            
            <div className="space-y-2">
              <label className="font-medium text-sm">Checkbox Option Value</label>
              <Input
                placeholder="Yes"
                value={rewardsSettings.checkboxOptionValue}
                onChange={(e) => setRewardsSettings({...rewardsSettings, checkboxOptionValue: e.target.value})}
              />
              <p className="text-xs text-gray-500">The option name/value that represents "enabled" (usually "Yes"). <strong>Case sensitive</strong> - must match exactly as it appears in GHL custom field options.</p>
            </div>
            
            <div className="space-y-2">
              <label className="font-medium text-sm">Date Field</label>
              <Input
                placeholder="Field ID"
                value={rewardsSettings.dateFieldId}
                onChange={(e) => setRewardsSettings({...rewardsSettings, dateFieldId: e.target.value})}
                className="font-mono text-xs"
              />
              {rewardsSettings.dateFieldName && (
                <p className="text-xs text-green-600">✓ {rewardsSettings.dateFieldName}</p>
              )}
              <p className="text-xs text-gray-500">The field ID from your CRM (use "Find Field IDs" button)</p>
            </div>
            
            <div className="space-y-2">
              <label className="font-medium text-sm">Folder ID (Optional)</label>
              <Input
                placeholder="Folder ID"
                value={rewardsSettings.folderId}
                onChange={(e) => setRewardsSettings({...rewardsSettings, folderId: e.target.value})}
                className="font-mono text-xs"
              />
              {rewardsSettings.folderName && (
                <div className="text-xs text-green-600 mt-1">
                  <p>✓ {rewardsSettings.folderName}</p>
                  {rewardsSettings.folderFieldNames && (
                    <p className="text-gray-600 ml-3 mt-0.5">Fields in folder: {rewardsSettings.folderFieldNames}</p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500">Filter to only show fields from this folder (leave blank to show all fields)</p>
            </div>
          </div>
          
          <Button
            className="w-full h-12 text-lg"
            onClick={async () => {
              try {
                // Save to database (shared across all users)
                await api.post('/settings/rewards', rewardsSettings);
                // Also save to localStorage for backward compatibility
                localStorage.setItem('rewards_settings', JSON.stringify(rewardsSettings));
                toast({
                  title: 'Success',
                  description: rewardsSettings.enabled ? 'Rewards system enabled and configured!' : 'Rewards system disabled'
                });
              } catch (error) {
                console.error('Failed to save rewards settings:', error);
                toast({
                  title: 'Error',
                  description: 'Failed to save rewards settings',
                  variant: 'destructive'
                });
              }
            }}
          >
            Save Rewards Settings
          </Button>
          
          {!rewardsSettings.enabled && (
            <p className="text-xs text-center text-gray-500 italic">
              Rewards features will be hidden throughout the POS when disabled
            </p>
          )}
        </CardContent>
      </Card>

      {/* Field Selector Dialog */}
      <Dialog open={showFieldSelector} onOpenChange={setShowFieldSelector}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Select Your Rewards Fields</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Checkbox Field (Rewards Status)</h3>
                  <p className="text-sm text-gray-600 mb-3">Select the checkbox field that tracks rewards membership</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-2">
                    {availableFields.filter(f => f.fieldType === 'CHECKBOX' || f.dataType === 'CHECKBOX').map((field) => (
                      <div
                        key={field.id}
                        className={`p-3 border rounded cursor-pointer hover:bg-blue-50 ${
                          rewardsSettings.checkboxFieldId === field.id ? 'bg-blue-100 border-blue-500' : ''
                        }`}
                        onClick={() => setRewardsSettings({
                          ...rewardsSettings,
                          checkboxFieldId: field.id,
                          checkboxFieldName: field.name
                        })}
                      >
                        <div className="font-medium">{field.name}</div>
                        <div className="text-xs text-gray-500">ID: {field.id}</div>
                        {field.parentId && (
                          <div className="text-xs text-purple-600 mt-1">Folder ID: {field.parentId}</div>
                        )}
                        {field.options && field.options.length > 0 && (
                          <div className="text-xs text-gray-600 mt-1">Options: {field.options.join(', ')}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Date Field (Enrollment Date)</h3>
                  <p className="text-sm text-gray-600 mb-3">Select the date field that tracks when customer joined</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-2">
                    {availableFields.filter(f => f.fieldType === 'DATE' || f.dataType === 'DATE').map((field) => (
                      <div
                        key={field.id}
                        className={`p-3 border rounded cursor-pointer hover:bg-green-50 ${
                          rewardsSettings.dateFieldId === field.id ? 'bg-green-100 border-green-500' : ''
                        }`}
                        onClick={() => setRewardsSettings({
                          ...rewardsSettings,
                          dateFieldId: field.id,
                          dateFieldName: field.name
                        })}
                      >
                        <div className="font-medium">{field.name}</div>
                        <div className="text-xs text-gray-500">ID: {field.id}</div>
                        {field.parentId && (
                          <div className="text-xs text-purple-600 mt-1">Folder ID: {field.parentId}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Folder ID (Optional - For Filtering)</h3>
                  <p className="text-sm text-gray-600 mb-3">Select a folder to only show fields from that folder on the Rewards page</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-2">
                    {/* Get unique folders from available fields */}
                    {Array.from(new Set(availableFields.map(f => f.parentId).filter(Boolean))).map((folderId) => {
                      const fieldsInFolder = availableFields.filter(f => f.parentId === folderId);
                      const folderName = `Folder (${fieldsInFolder.length} fields)`;
                      const fieldNames = fieldsInFolder.slice(0, 3).map(f => f.name).join(', ');
                      const hasMore = fieldsInFolder.length > 3;
                      const folderDisplayText = hasMore ? `${fieldNames}...` : fieldNames;
                      return (
                        <div
                          key={folderId}
                          className={`p-3 border rounded cursor-pointer hover:bg-purple-50 ${
                            rewardsSettings.folderId === folderId ? 'bg-purple-100 border-purple-500' : ''
                          }`}
                          onClick={() => setRewardsSettings({
                            ...rewardsSettings,
                            folderId: folderId,
                            folderName: folderName,
                            folderFieldNames: folderDisplayText
                          })}
                        >
                          <div className="font-medium">{folderName}</div>
                          <div className="text-xs text-gray-500">Folder ID: {folderId}</div>
                          <div className="text-xs text-gray-600 mt-1">
                            Fields: {fieldsInFolder.map(f => f.name).join(', ')}
                          </div>
                        </div>
                      );
                    })}
                    {Array.from(new Set(availableFields.map(f => f.parentId).filter(Boolean))).length === 0 && (
                      <p className="text-sm text-gray-500 p-3">No folders found. All fields are at root level.</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowFieldSelector(false)}>
                  Cancel
                </Button>
                <Button onClick={() => {
                  setShowFieldSelector(false);
                  toast({
                    title: 'Fields Selected',
                    description: 'Remember to save your rewards settings!'
                  });
                }}>
                  Done
                </Button>
              </div>
            </DialogContent>
          </Dialog>

      {/* Rewards Instructions Modal */}
      <Dialog open={showRewardsInstructions} onOpenChange={setShowRewardsInstructions}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Customer Rewards Setup Instructions</span>
              <Button
                variant="outline"
                size="sm"
                onClick={printRewardsInstructions}
                className="ml-4"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Instructions
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 text-sm">
            <div>
              <h3 className="font-semibold text-base mb-2">Overview</h3>
              <p className="text-gray-700">
                The Castle POS integrates with your CRM's custom fields to manage customer rewards. 
                You'll need to create two custom fields in your CRM account before using the rewards feature.
              </p>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <h4 className="font-semibold text-blue-900 mb-2">📋 Important: What This Feature Does</h4>
              <p className="text-sm text-blue-800 mb-2"><strong>Castle POS provides the enrollment interface:</strong></p>
              <ul className="text-sm text-blue-800 space-y-1 mb-3">
                <li>• Enroll customers into your rewards program</li>
                <li>• View all enrolled rewards members</li>
                <li>• Look up customer information quickly</li>
              </ul>
              <p className="text-sm text-blue-800">
                <strong>You control the rewards tracking:</strong> The actual rewards program (points, visit tracking, special offers, etc.) 
                is managed through your CRM using custom fields and automations. The POS simply marks customers as "enrolled" - 
                you define what rewards they get and how to track them using your CRM's automation features.
              </p>
            </div>
            
            <div className="border-t pt-4">
              <h3 className="font-semibold text-base mb-3">Requirements</h3>
              <p className="text-gray-700 mb-3">
                <strong>You need to create two custom fields in your CRM:</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>1. Checkbox Field</strong> - Tracks rewards enrollment status</li>
                <li>• <strong>2. Date Picker Field</strong> - Records enrollment date</li>
              </ul>
              <p className="text-sm text-gray-600 mt-3 italic">
                Note: You can name these fields anything you want. Use the "Find Field IDs" button to select them after creation.
              </p>
              
              <h4 className="font-semibold text-base mt-4 mb-2">Recommended Field Setup (Examples)</h4>
              <div className="space-y-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h5 className="font-semibold text-sm mb-1">Example 1: Checkbox Field</h5>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• <strong>Field Name:</strong> "Activate Rewards Member" (Your choice)</li>
                    <li>• <strong>Option Name:</strong> "Yes" (Your choice)</li>
                    <li>• <strong>Example Unique ID:</strong> <code className="bg-white px-1 py-0.5 rounded text-xs">contact.activate_rewards_member</code></li>
                  </ul>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg">
                  <h5 className="font-semibold text-sm mb-1">Example 2: Date Picker Field</h5>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• <strong>Field Name:</strong> "Member Since" (Your choice)</li>
                    <li>• <strong>Example Unique ID:</strong> <code className="bg-white px-1 py-0.5 rounded text-xs">contact.member_since</code></li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h3 className="font-semibold text-base mb-2">How to Set Up in Your CRM</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Log into your CRM account</li>
                <li>Navigate to <strong>Settings → Custom Fields</strong></li>
                <li>
                  <strong>Create a Custom Fields Folder (Recommended):</strong>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1 text-sm">
                    <li>Click "Add Folder"</li>
                    <li>Name it "Customer Rewards" or "POS Fields" (Your choice)</li>
                    <li>This keeps your rewards fields organized</li>
                    <li>The POS will return all fields in this folder</li>
                  </ul>
                </li>
                <li>Click <strong>"Add Field"</strong></li>
                <li>Create a <strong>Checkbox field</strong> with option "Yes" (Your choice)</li>
                <li>Create a <strong>Date field</strong> (for enrollment date) - Optionally assign to your "Customer Rewards" folder</li>
                <li>Return to the POS settings page</li>
                <li>Click the <strong>"Find Field IDs"</strong> button</li>
                <li>Select your fields from the list</li>
                <li>Save your rewards settings</li>
              </ol>
            </div>
            
            <div className="border-t pt-4">
              <h3 className="font-semibold text-base mb-2">How the POS Uses These Fields</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• When you enroll a customer in rewards, the POS checks the checkbox field</li>
                <li>• The enrollment date is automatically set to today's date</li>
                <li>• The Rewards page shows only customers with the checkbox enabled</li>
                <li>• You can view and edit customer details from the Rewards page</li>
              </ul>
              
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-semibold text-sm mb-2 text-purple-900">💡 Pro Tip: Automate with Your CRM</h4>
                <p className="text-sm text-purple-800">
                  Use your CRM's automation features to create powerful workflows based on the rewards checkbox! 
                  You can automatically:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-purple-800">
                  <li>• Track total customer lifetime spend for rewards members</li>
                  <li>• Monitor and record last visit dates</li>
                  <li>• Send automated birthday or anniversary messages</li>
                  <li>• Create special offers for active rewards members</li>
                  <li>• Build segments and campaigns targeting only rewards customers</li>
                  <li>• Trigger follow-up sequences after purchases</li>
                </ul>
                <p className="text-sm text-purple-800 mt-2">
                  <strong>How:</strong> Use the checkbox field as a filter, along with a trigger in your CRM automations. 
                  For example, set the trigger as "Payment Received" and add an "If" condition: "If Customer Rewards is Yes" - 
                  then you can execute any actions after that! Only contacts with this checkbox enabled will be rewards members, 
                  making it easy to target them with specific workflows.
                </p>
              </div>
            </div>
          </div>
          
          {/* Bottom Print Button */}
          <div className="flex justify-center gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={printRewardsInstructions}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Instructions
            </Button>
            <Button onClick={() => setShowRewardsInstructions(false)}>
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Settings Card - Admin Only */}
      {isAdmin() && (
      <Card>
        <CardHeader><CardTitle>Receipt Print Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Preview Section */}
          <div className="bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-300">
            <div className="text-sm font-semibold text-gray-700 mb-3 text-center">Receipt Preview</div>
            <div 
              className="bg-white mx-auto shadow-md" 
              style={{ 
                width: '300px',
                fontFamily: 'Courier New, monospace',
                fontSize: '11px',
                padding: '5mm',
                lineHeight: '1.3'
              }}
            >
              {/* Header */}
              <div className="text-center">
                {(receiptSettings.showLogo !== false) && brand?.logo_url && (
                  <img 
                    src={brand.logo_url} 
                    alt="Logo" 
                    className="mx-auto mb-2"
                    style={{ maxWidth: '150px', maxHeight: '80px' }}
                  />
                )}
                <div className="font-bold text-base mb-1">{brand?.business_name || 'Castle POS'}</div>
                {receiptSettings.showPhone && <div className="text-xs">Phone: {(() => {
                  const phone = brand?.phone || '+15551234567';
                  const digits = phone.replace(/\D/g, '');
                  const cleaned = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
                  return cleaned.length === 10 ? `${cleaned.slice(0,3)}-${cleaned.slice(3,6)}-${cleaned.slice(6)}` : phone;
                })()}</div>}
                {receiptSettings.showEmail && <div className="text-xs">{brand?.email || 'info@business.com'}</div>}
                <div className="border-t border-dashed border-gray-400 my-2"></div>
                <div className="text-xs">Receipt #INV-12345</div>
                <div className="text-xs">2024-01-15 10:30:00</div>
              </div>
              
              <div className="border-t border-dashed border-gray-400 my-2"></div>
              
              {/* Items */}
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Sample Product</span>
                </div>
                <div className="flex justify-between pl-2 text-[10px]">
                  <span>2 × $25.00</span>
                  <span>$50.00</span>
                </div>
              </div>
              
              <div className="border-t border-dashed border-gray-400 my-2"></div>
              
              {/* Totals */}
              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>$50.00</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span>$3.75</span>
                </div>
                <div className="flex justify-between font-bold text-sm mt-1 pt-1 border-t border-gray-400">
                  <span>TOTAL:</span>
                  <span>$53.75</span>
                </div>
              </div>
              
              <div className="border-t border-dashed border-gray-400 my-2"></div>
              
              {/* Payment */}
              <div className="text-xs flex justify-between">
                <span>Payment Method:</span>
                <span className="font-bold">CASH</span>
              </div>
              
              <div className="border-t border-dashed border-gray-400 my-2"></div>
              
              {/* Footer */}
              <div className="text-center text-xs mt-3">
                {(receiptSettings.thankYouMessage || 'Thank You!\nPlease come again').split('\n').map((line, idx) => (
                  <div key={idx} className={idx === 0 ? "font-bold" : "text-[10px] mt-1"}>{line}</div>
                ))}
              </div>
              
              {/* Barcode */}
              {receiptSettings.showBarcode && (
                <div className="text-center mt-3">
                  <div className="bg-gray-200 h-10 flex items-center justify-center text-[10px] text-gray-600">
                    [BARCODE]
                  </div>
                  <div className="text-[9px] mt-1 tracking-widest">INV-12345</div>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-3">
            {/* Admin-Only: Receipt Content Settings */}
            {isAdmin() && (
              <>
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <p className="text-sm font-medium text-blue-900">Admin Settings</p>
                  <p className="text-xs text-blue-700">These settings control what appears on all receipts</p>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Show Logo</div>
                    <div className="text-sm text-gray-600">Display business logo on receipt</div>
                  </div>
                  <Switch 
                    checked={receiptSettings.showLogo}
                    onCheckedChange={(checked) => {
                      const newSettings = {...receiptSettings, showLogo: checked};
                      setReceiptSettings(newSettings);
                      localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                    }}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Show Phone Number</div>
                    <div className="text-sm text-gray-600">Display business phone on receipt</div>
                  </div>
                  <Switch 
                    checked={receiptSettings.showPhone}
                    onCheckedChange={(checked) => {
                      const newSettings = {...receiptSettings, showPhone: checked};
                      setReceiptSettings(newSettings);
                      localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                    }}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Show Email Address</div>
                    <div className="text-sm text-gray-600">Display business email on receipt</div>
                  </div>
                  <Switch 
                    checked={receiptSettings.showEmail}
                    onCheckedChange={(checked) => {
                      const newSettings = {...receiptSettings, showEmail: checked};
                      setReceiptSettings(newSettings);
                      localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                    }}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Show Invoice Barcode</div>
                    <div className="text-sm text-gray-600">Add scannable barcode for invoice lookup</div>
                  </div>
                  <Switch 
                    checked={receiptSettings.showBarcode}
                    onCheckedChange={(checked) => {
                      const newSettings = {...receiptSettings, showBarcode: checked};
                      setReceiptSettings(newSettings);
                      localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Allow Complete Without Printing</div>
                    <div className="text-sm text-gray-600">Option to complete order without printing receipt</div>
                  </div>
                  <Switch 
                    checked={receiptSettings.allowCompleteWithoutPrinting}
                    onCheckedChange={(checked) => {
                      const newSettings = {...receiptSettings, allowCompleteWithoutPrinting: checked};
                      setReceiptSettings(newSettings);
                      localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                    }}
                  />
                </div>
                
                <div className="flex flex-col gap-1 border-t border-b py-4 my-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-amber-600 flex items-center gap-2">
                        Enable Background Print Worker (Beta)
                      </div>
                      <div className="text-sm text-gray-600">Keep print service running in background for nearly instant printing.</div>
                    </div>
                    <Switch 
                      checked={receiptSettings.useBackgroundWorker || false}
                      onCheckedChange={(checked) => {
                        const newSettings = {...receiptSettings, useBackgroundWorker: checked};
                        setReceiptSettings(newSettings);
                        localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                      }}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="font-medium">Thank You Message</label>
                  <textarea 
                    className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm"
                    placeholder="Thank You!&#10;Please come again (leave blank to hide)"
                    value={receiptSettings.thankYouMessage || ''}
                    onChange={(e) => {
                      const newSettings = {...receiptSettings, thankYouMessage: e.target.value};
                      setReceiptSettings(newSettings);
                      localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                    }}
                  />
                  <p className="text-xs text-gray-500">Use line breaks for multiple lines. Leave blank to hide message entirely.</p>
                </div>
                
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Layout Settings (can be adjusted by POS users per device)</p>
                </div>
              </>
            )}
            
            {/* POS Users see this section header */}
            {!isAdmin() && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-900">Device-Specific Settings</p>
                <p className="text-xs text-gray-600">Adjust margins for this device/printer</p>
              </div>
            )}
            
            {/* Both Admin and POS Users: Margins (Left and Right only) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="font-medium">Left Margin (mm)</label>
                <Input 
                  type="number"
                  min="-10"
                  max="20"
                  step="0.25"
                  value={receiptSettings.leftMargin || 0}
                  onChange={(e) => {
                    const newSettings = {...receiptSettings, leftMargin: parseFloat(e.target.value) || 0};
                    setReceiptSettings(newSettings);
                    localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                  }}
                  className="text-center"
                />
                <p className="text-xs text-gray-500">Negative shifts left (0.25mm steps)</p>
              </div>
              
              <div className="space-y-2">
                <label className="font-medium">Right Margin (mm)</label>
                <Input 
                  type="number"
                  min="-10"
                  max="20"
                  step="0.25"
                  value={receiptSettings.rightMargin || 0}
                  onChange={(e) => {
                    const newSettings = {...receiptSettings, rightMargin: parseFloat(e.target.value) || 0};
                    setReceiptSettings(newSettings);
                    localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
                  }}
                  className="text-center"
                />
                <p className="text-xs text-gray-500">Negative shifts right (0.25mm steps)</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="font-medium">Select Printer</label>
              <div className="flex gap-2">
                <select 
                  className="flex-1 h-10 px-3 py-2 border rounded-md text-sm"
                  value={receiptSettings.selectedPrinter}
                  onChange={(e) => setReceiptSettings({...receiptSettings, selectedPrinter: e.target.value})}
                >
                  <option value="">Default Printer</option>
                  {availablePrinters.map((printer, idx) => (
                    <option key={idx} value={printer}>{printer}</option>
                  ))}
                </select>
                <Button 
                  variant="outline"
                  onClick={loadPrinters}
                  disabled={loadingPrinters}
                  className="whitespace-nowrap"
                >
                  {loadingPrinters ? 'Loading...' : 'Refresh Printers'}
                </Button>
              </div>
              <p className="text-xs text-gray-500">Select which printer to use for receipts</p>
            </div>
            
            {/* Print Test Receipt Button */}
            <Button
              onClick={printTestReceipt}
              variant="outline"
              className="w-full h-12 text-base font-semibold border-green-300 text-green-700 hover:bg-green-50"
            >
              <Printer className="h-5 w-5 mr-2" />
              Print Test Receipt
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Print a sample receipt to test your margin and printer settings
            </p>
            
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Open Cash Drawer</div>
                  <div className="text-sm text-gray-600">Automatically kick cash drawer on cash/check payments</div>
                </div>
                <Switch 
                  checked={receiptSettings.openCashDrawer !== false}
                  onCheckedChange={(checked) => setReceiptSettings({...receiptSettings, openCashDrawer: checked})}
                />
              </div>
              
              {/* Test Cash Drawer Button */}
              <Button
                onClick={async () => {
                  try {
                    console.log('[Settings] Testing cash drawer kick...');
                    
                    // Initialize printing service
                    await unifiedPrintingService.initialize();
                    
                    // Use the built-in openCashDrawer method
                    const printerName = receiptSettings.selectedPrinter;
                    await unifiedPrintingService.openCashDrawer(printerName);
                    
                    toast({
                      title: 'Cash Drawer Test',
                      description: 'Cash drawer kick command sent successfully!'
                    });
                  } catch (error) {
                    console.error('[Settings] Cash drawer test failed:', error);
                    toast({
                      title: 'Test Failed',
                      description: error.message || 'Failed to send cash drawer command',
                      variant: 'destructive'
                    });
                  }
                }}
                variant="outline"
                className="w-full h-10 text-sm border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                Test Cash Drawer Kick
              </Button>
            </div>
          </div>
          
          <Button 
            className="w-full h-12 text-lg"
            onClick={async () => {
              try {
                // Save admin settings (shared across location)
                await api.put('/settings/admin', {
                  receipt_settings: {
                    show_logo: receiptSettings.showLogo,
                    show_phone: receiptSettings.showPhone,
                    show_email: receiptSettings.showEmail,
                    show_barcode: receiptSettings.showBarcode,
                    thank_you_message: receiptSettings.thankYouMessage,
                    allow_complete_without_printing: receiptSettings.allowCompleteWithoutPrinting,
                    use_background_worker: receiptSettings.useBackgroundWorker || false
                  },
                  last_selected_printer: receiptSettings.selectedPrinter || null
                });
                
                // Save device settings (printer-specific) if a printer is selected
                if (receiptSettings.selectedPrinter) {
                  await api.put('/settings/device', {
                    printer_name: receiptSettings.selectedPrinter,
                    left_margin: receiptSettings.leftMargin,
                    right_margin: receiptSettings.rightMargin,
                    open_cash_drawer: receiptSettings.openCashDrawer
                  });
                }
                
                // Also save to localStorage for backward compatibility and offline fallback
                localStorage.setItem('receipt_settings', JSON.stringify(receiptSettings));
                
                toast({ 
                  title: 'Success', 
                  description: 'Receipt settings saved successfully!' 
                });
              } catch (error) {
                console.error('Failed to save receipt settings:', error);
                toast({ 
                  title: 'Error', 
                  description: 'Failed to save receipt settings', 
                  variant: 'destructive' 
                });
              }
            }}
          >
            Save Receipt Settings
          </Button>
          
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 text-sm" data-testid="qz-status">
              <div className={`h-2 w-2 rounded-full ${qzConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="font-medium">QZ Tray:</span>
              <span className={qzConnected ? 'text-green-600' : 'text-red-600'}>
                {qzConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-4">
              QZ Tray is required for printing receipts to your thermal printer. {!qzConnected && 'Please make sure QZ Tray is running on your computer.'}
            </p>
          </div>
        </CardContent>
      </Card>
      )}
      </>
      )}
    </div>
  );
}
